/**
 * Ticket detail — markdown body with in-place editing, the `verify` command,
 * links grouped by type, the comment thread and the full event history.
 *
 * Every mutation goes through `PATCH /api/v1/tickets/{key}`, which writes one
 * audit event per changed field; the response is the refreshed detail payload,
 * so the history below updates without a second request.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api.js'
import AskClaude from './AskClaude.jsx'
import { Chip, CopyButton, Markdown, Priority } from '../components/bits.jsx'
import { KINDS, PRIORITIES, STATUSES, STATUS_LABELS, canTransition } from '../lib/filters.js'
import { ago, stamp, userName } from '../lib/format.js'
import { useTaxonomy } from '../lib/taxonomy.jsx'

/** Human labels for the four link axes, rendered from this ticket's viewpoint. */
const LINK_GROUPS = [
  ['blocked_by', 'Blocked by'],
  ['blocks', 'Blocks'],
  ['parent', 'Parent / epic'],
  ['relates_to', 'Relates to'],
]

function Field({ label, children }) {
  return (
    <div className="side-field">
      <label>{label}</label>
      {children}
    </div>
  )
}

/** Click-to-edit text, committed on blur or Enter (Cmd+Enter for multiline). */
function EditableText({ value, multiline, placeholder, onSave, className }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')

  useEffect(() => {
    if (!editing) setDraft(value || '')
  }, [value, editing])

  const commit = async () => {
    setEditing(false)
    if ((value || '') !== draft) await onSave(draft)
  }

  if (!editing) {
    return (
      <div
        className={className}
        onDoubleClick={() => setEditing(true)}
        title="Double-click to edit"
        style={{ cursor: 'text' }}
      >
        {value ? (
          multiline ? (
            <Markdown source={value} />
          ) : (
            value
          )
        ) : (
          <span className="kbdhint">{placeholder}</span>
        )}
      </div>
    )
  }

  return multiline ? (
    <div>
      <textarea
        className="textarea"
        rows={Math.max(8, draft.split('\n').length + 2)}
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setDraft(value || '')
            setEditing(false)
          }
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) commit()
        }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button type="button" className="btn sm primary" onClick={commit}>
          Save
        </button>
        <button
          type="button"
          className="btn sm ghost"
          onClick={() => {
            setDraft(value || '')
            setEditing(false)
          }}
        >
          Cancel
        </button>
        <span className="kbdhint" style={{ alignSelf: 'center' }}>
          <kbd>⌘</kbd>+<kbd>enter</kbd> saves
        </span>
      </div>
    </div>
  ) : (
    <input
      className="input"
      style={{ width: '100%', height: 30, fontSize: 18 }}
      autoFocus
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') commit()
        if (event.key === 'Escape') {
          setDraft(value || '')
          setEditing(false)
        }
      }}
    />
  )
}

/** Multi-select over a taxonomy axis (cities / components / assets). */
function AxisSelect({ label, options, selectedIds, labelOf, onChange }) {
  return (
    <Field label={label}>
      <select
        className="select"
        multiple
        size={Math.min(6, Math.max(3, options.length))}
        style={{ height: 'auto', padding: 4 }}
        value={selectedIds.map(String)}
        onChange={(event) =>
          onChange(Array.from(event.target.selectedOptions).map((o) => Number(o.value)))
        }
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {labelOf(option)}
          </option>
        ))}
      </select>
    </Field>
  )
}

export default function Ticket() {
  const { key } = useParams()
  const navigate = useNavigate()
  const { users, usersById, cities, components, assets } = useTaxonomy()
  const [ticket, setTicket] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [comment, setComment] = useState('')
  const [linkForm, setLinkForm] = useState({ type: 'blocked_by', other: '' })
  const [editVerify, setEditVerify] = useState(false)
  const [verifyDraft, setVerifyDraft] = useState('')

  const load = useCallback(() => {
    api
      .ticket(key)
      .then((data) => {
        setTicket(data)
        setVerifyDraft(data.verify || '')
        setError(null)
      })
      .catch(setError)
  }, [key])

  useEffect(() => {
    setTicket(null)
    load()
  }, [load])

  const patch = async (body) => {
    setBusy(true)
    try {
      const updated = await api.updateTicket(key, body)
      setTicket(updated)
      setError(null)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  if (error && !ticket) return <div className="notice error">{error.message}</div>
  if (!ticket) return <div className="notice">Loading {key}…</div>

  const isDecision = ticket.kind === 'scope-decision' || ticket.kind === 'go-live-gate'
  const ownerField = isDecision ? 'decider_id' : 'assignee_id'
  const ownerValue = isDecision ? ticket.decider_id : ticket.assignee_id

  const addComment = async (event) => {
    event.preventDefault()
    if (!comment.trim()) return
    setBusy(true)
    try {
      await api.addComment(key, comment)
      setComment('')
      load()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const addLink = async (event) => {
    event.preventDefault()
    const other = linkForm.other.trim().toUpperCase()
    if (!other) return
    setBusy(true)
    try {
      await api.createLink(ticket.key, other, linkForm.type)
      setLinkForm({ ...linkForm, other: '' })
      load()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const removeLink = async (id) => {
    setBusy(true)
    try {
      await api.deleteLink(id)
      load()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="detail">
      <div className="detail-main">
        <div className="crumbs">
          <Link to="/">Board</Link>
          <span>/</span>
          <span className="key">{ticket.key}</span>
          <Priority value={ticket.priority} />
          <Chip kind="kind">{ticket.kind}</Chip>
          <Chip>{STATUS_LABELS[ticket.status]}</Chip>
          <span className="spacer" />
          {busy && <span className="kbdhint">saving…</span>}
        </div>

        <h1>
          <EditableText
            value={ticket.title}
            placeholder="untitled"
            onSave={(title) => patch({ title })}
          />
        </h1>

        {error && <div className="error" style={{ marginBottom: 12 }}>{error.message}</div>}

        {ticket.closed_unverified && (
          <div className="unverified-banner" role="status">
            <strong>Closed without evidence.</strong>{' '}
            {ticket.unverified_reason || 'No reason was recorded.'}{' '}
            <span className="text-muted">
              Re-open and close again with the real output of the verify command to clear this.
            </span>
          </div>
        )}

        {ticket.claimed_by_id && (
          <div className="claim-banner" role="status">
            <strong>Claimed</strong> by {userName(usersById, ticket.claimed_by_id)}
            {ticket.claim_note ? ` — ${ticket.claim_note}` : ''}
            {ticket.claimed_at ? ` · ${ago(ticket.claimed_at)} ago` : ''}
            <span className="spacer" />
            <button
              type="button"
              className="btn sm ghost"
              onClick={async () => {
                await api.releaseTicket(key)
                load()
              }}
            >
              Release
            </button>
          </div>
        )}

        <div className="section">
          <h2>Verify — the command that proves this is done</h2>
          <div className="verify">
            <div className="verify-head">
              {ticket.verify ? 'verify' : 'verify — not set'}
              <span className="spacer" />
              {ticket.verify && <CopyButton text={ticket.verify} />}
              <button type="button" className="btn sm ghost" onClick={() => setEditVerify((v) => !v)}>
                {editVerify ? 'Cancel' : ticket.verify ? 'Edit' : 'Set'}
              </button>
            </div>
            {editVerify ? (
              <div style={{ padding: 10 }}>
                <textarea
                  className="textarea"
                  rows={3}
                  autoFocus
                  value={verifyDraft}
                  onChange={(event) => setVerifyDraft(event.target.value)}
                />
                <button
                  type="button"
                  className="btn sm primary"
                  style={{ marginTop: 6 }}
                  onClick={async () => {
                    await patch({ verify: verifyDraft || null })
                    setEditVerify(false)
                  }}
                >
                  Save
                </button>
              </div>
            ) : (
              <pre>{ticket.verify || 'No verification command recorded for this ticket.'}</pre>
            )}
          </div>
          {ticket.verify_output && (
            <div className="verify" style={{ marginTop: 8 }}>
              <div className="verify-head">evidence recorded at close</div>
              <pre>{ticket.verify_output}</pre>
            </div>
          )}
        </div>

        <div className="section">
          <h2>Description</h2>
          <EditableText
            value={ticket.body}
            multiline
            placeholder="Double-click to write a markdown description"
            onSave={(body) => patch({ body })}
          />
        </div>

        <div className="section">
          <h2>Decision</h2>
          <EditableText
            value={ticket.decision}
            multiline
            placeholder="Double-click to record what was decided, and by whom"
            onSave={(decision) => patch({ decision })}
          />
        </div>

        <div className="section">
          <h2>Rejected approaches</h2>
          <div className="kbdhint">
            What was tried and measurably failed — so it is not re-litigated.
          </div>
          <EditableText
            value={ticket.rejected}
            multiline
            placeholder="Double-click to record an approach that failed, and the measured reason"
            onSave={(rejected) => patch({ rejected })}
          />
        </div>

        {(ticket.source_doc || ticket.source_anchor) && (
          <div className="section">
            <h2>Provenance</h2>
            <div className="kbdhint">
              {ticket.source_doc}
              {ticket.source_anchor ? ` · ${ticket.source_anchor}` : ''}
            </div>
          </div>
        )}

        <div className="section">
          <h2>Links</h2>
          {LINK_GROUPS.map(([type, label]) => {
            const rows = ticket.links.filter((l) => l.type === type)
            if (!rows.length) return null
            return (
              <div className="linkgroup" key={type}>
                <h3>{label}</h3>
                {rows.map((row) => (
                  <div className="linkrow" key={row.link_id}>
                    <Link to={`/t/${row.other_key}`}>
                      <span className="key">{row.other_key}</span>{' '}
                      <span className="lt">open</span>
                    </Link>
                    <span className="spacer" />
                    <button
                      type="button"
                      className="btn sm ghost"
                      onClick={() => removeLink(row.link_id)}
                    >
                      unlink
                    </button>
                  </div>
                ))}
              </div>
            )
          })}
          <form onSubmit={addLink} style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <select
              className="select"
              value={linkForm.type}
              onChange={(event) => setLinkForm({ ...linkForm, type: event.target.value })}
            >
              {LINK_GROUPS.map(([type, label]) => (
                <option key={type} value={type}>
                  {label}
                </option>
              ))}
            </select>
            <input
              className="input"
              placeholder="ticket key, e.g. LAH-14"
              value={linkForm.other}
              onChange={(event) => setLinkForm({ ...linkForm, other: event.target.value })}
            />
            <button type="submit" className="btn sm">
              Link
            </button>
          </form>
        </div>

        <div className="section">
          <AskClaude ticketKey={ticket.key} onAnswered={load} />
        </div>

        <div className="section">
          <h2>Comments ({ticket.comments.length})</h2>
          {ticket.comments.map((row) => (
            <div className="comment" key={row.id}>
              <div className="comment-meta">
                {userName(usersById, row.author_id)}
                {row.actor_kind === 'agent' && (
                  <span className="agent-tag" title="written by an agent through an API token">
                    agent
                  </span>
                )}{' '}
                · {stamp(row.created_at)} ({ago(row.created_at)} ago)
              </div>
              <Markdown source={row.body} />
            </div>
          ))}
          <form onSubmit={addComment} style={{ marginTop: 10 }}>
            <textarea
              className="textarea"
              rows={3}
              placeholder="Comment (markdown). ⌘+enter to post."
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) addComment(event)
              }}
            />
            <button type="submit" className="btn sm primary" style={{ marginTop: 6 }} disabled={busy}>
              Comment
            </button>
          </form>
        </div>

        <div className="section">
          <h2>History ({ticket.events.length})</h2>
          <ul className="events">
            {ticket.events.map((event) => (
              <li key={event.id}>
                <span className="at">{stamp(event.at)}</span>
                {event.field === 'created' ? (
                  <span>
                    <b>{userName(usersById, event.actor_id)}</b> created{' '}
                    <code>{event.new_value}</code>
                  </span>
                ) : (
                <span>
                  <b>{userName(usersById, event.actor_id)}</b> changed <b>{event.field}</b>{' '}
                  {event.old_value ? (
                    <>
                      from <code>{event.old_value}</code>{' '}
                    </>
                  ) : null}
                  to <code>{event.new_value === null ? '∅' : event.new_value}</code>
                </span>
                )}
              </li>
            ))}
            {ticket.events.length === 0 && <li>No recorded changes yet.</li>}
          </ul>
        </div>
      </div>

      <div className="detail-side">
        <Field label="Status">
          <select
            className="select"
            value={ticket.status}
            onChange={(event) => patch({ status: event.target.value })}
          >
            {STATUSES.map((status) => (
              <option
                key={status}
                value={status}
                disabled={!canTransition(ticket.status, status)}
              >
                {STATUS_LABELS[status]}
                {canTransition(ticket.status, status) ? '' : ' (blocked)'}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Priority">
          <select
            className="select"
            value={ticket.priority}
            onChange={(event) => patch({ priority: event.target.value })}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p.toUpperCase()}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Kind">
          <select
            className="select"
            value={ticket.kind}
            onChange={(event) => patch({ kind: event.target.value })}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </Field>

        <Field label={isDecision ? 'Decider' : 'Assignee'}>
          <select
            className="select"
            value={ownerValue == null ? '' : String(ownerValue)}
            onChange={(event) =>
              patch({ [ownerField]: event.target.value === '' ? null : Number(event.target.value) })
            }
          >
            <option value="">{isDecision ? 'no decider' : 'unassigned'}</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name || user.email}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Reporter">
          <div className="kbdhint">{userName(usersById, ticket.reporter_id)}</div>
        </Field>

        <Field label="Due date">
          <input
            className="input"
            type="date"
            value={ticket.due_date || ''}
            onChange={(event) => patch({ due_date: event.target.value || null })}
          />
        </Field>

        <Field label="Estimate (days)">
          <input
            className="input"
            type="number"
            step="0.5"
            value={ticket.estimate ?? ''}
            onChange={(event) =>
              patch({ estimate: event.target.value === '' ? null : Number(event.target.value) })
            }
          />
        </Field>

        <AxisSelect
          label="Cities"
          options={cities}
          selectedIds={ticket.cities.map((c) => c.id)}
          labelOf={(c) => c.name}
          onChange={(city_ids) => patch({ city_ids })}
        />
        <AxisSelect
          label="Components"
          options={components}
          selectedIds={ticket.components.map((c) => c.id)}
          labelOf={(c) => c.name}
          onChange={(component_ids) => patch({ component_ids })}
        />
        <AxisSelect
          label="Assets"
          options={assets}
          selectedIds={ticket.assets.map((a) => a.id)}
          labelOf={(a) => `${a.kind}: ${a.model}${a.serial ? ` (${a.serial})` : ''}`}
          onChange={(asset_ids) => patch({ asset_ids })}
        />

        <Field label="Timestamps">
          <div className="kbdhint">
            created {stamp(ticket.created_at)}
            <br />
            updated {stamp(ticket.updated_at)} ({ago(ticket.updated_at)} ago)
            {ticket.closed_at ? (
              <>
                <br />
                closed {stamp(ticket.closed_at)}
              </>
            ) : null}
          </div>
        </Field>

        <button type="button" className="btn sm" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
    </div>
  )
}
